/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : userManagent.js
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
/**
 * @author Tahir Shaik - tahir@au79consulting.com
 * @description  User Management API details to perform a particular action
 * @date 06-07-2024
 */
import actionType from "../actionTypes";
const userManagementApis = {
  // Country Codes
  get_country_codes: {
    api_name: "/UCM/user/countries",
    https_method: "GET",
    action_type: actionType.GET_COUNTRY_CODES,
    description: "",
  },
  add_user: {
    api_name: "/UCM/user/add/account",
    https_method: "POST",
    action_type: actionType.ADD_USER,
    description: "",
  },
  get_users_list_pa: {
    api_name: "/UCM/user/all",
    https_method: "GET",
    action_type: actionType.GET_USERS_LIST_PA,
    description: "To get all the users and their details",
  },
  get_users_list: {
    api_name: "/UCM/user/all",
    https_method: "GET",
    action_type: actionType.GET_USERS_LIST,
    description: "To get all the users and their details",
  },
  get_user_assigned_roles: {
    api_name: "/PCM/project/access/roles",
    https_method: "GET",
    action_type: "",
    description: "Get roles assigned to a user",
  },
  get_user_project_access_list: {
    api_name: "/PCM/project/access/list",
    https_method: "GET",
    action_type: "",
    description: "Get project access list for user and role",
  },
  update_user: {
    api_name: "/UCM/user/admin/profile/update",
    https_method: "PUT",
    action_type: actionType.UPDATE_USER,
    description: "To get all the users and their details",
  },
  change_user_status: {
    api_name: "/UCM/user/status",
    https_method: "PUT",
    action_type: actionType.CHANGE_USER_STATUS,
    description: "",
  },
  delete_user: {
    api_name: "/UCM/user/delete",
    https_method: "PUT",
    action_type: actionType.DELETE_USER,
    description: "",
  },
  change_user_role: {
    api_name: "/UCM/user/change/role",
    https_method: "PUT",
    action_type: actionType.CHANGE_USER_ROLE,
    description: "",
  },
  get_user_change_logs: {
    api_name: "/UCM/user/change-logs",
    https_method: "GET",
    action_type: "",
    description: "Get the user change logs",
  },
  // Roles
  add_role: {
    api_name: "/UCM/roles/add",
    https_method: "POST",
    action_type: actionType.ADD_ROLE,
    description: "",
  },
  get_roles_list: {
    api_name: "/UCM/roles/all",
    https_method: "GET",
    action_type: actionType.GET_ROLES_LIST,
    description: "",
  },
  get_roles_list_main: {
    api_name: "/UCM/roles/all",
    https_method: "GET",
    action_type: actionType.GET_ROLES_LIST_MAIN,
    description: "",
  },
  update_role: {
    api_name: "/UCM/roles/update",
    https_method: "PUT",
    action_type: actionType.UPDATE_ROLE,
    description: "",
  },
  remove_chapters: {
    api_name: "/UCM/roles/remove/legal-chapters",
    https_method: "PUT",
    action_type: actionType.REMOVE_CHAPTERS,
    description: "",
  },
  change_role_status: {
    api_name: "/UCM/roles/status",
    https_method: "PUT",
    action_type: actionType.CHANGE_ROLE_STATUS,
    description: "",
  },
  delete_role: {
    api_name: "/UCM/roles/delete",
    https_method: "PUT",
    action_type: actionType.DELETE_ROLE,
    description: "",
  },
  add_page_access: {
    api_name: "/UCM/roles/add/access/pages",
    https_method: "PUT",
    action_type: actionType.ADD_PAGE_ACCESS,
    description: "",
  },
  remove_page_access: {
    api_name: "/UCM/roles/remove/access/pages",
    https_method: "PUT",
    action_type: actionType.REMOVE_PAGE_ACCESS,
    description: "",
  },
  add_feature_access: {
    api_name: "/UCM/roles/add/access/features",
    https_method: "PUT",
    action_type: actionType.ADD_FEATURE_ACCESS,
    description: "",
  },
  remove_feature_access: {
    api_name: "/UCM/roles/remove/access/features",
    https_method: "PUT",
    action_type: actionType.REMOVE_FEATURE_ACCESS,
    description: "",
  },
  // Features
  get_features_list: {
    api_name: "/UCM/features/all",
    https_method: "GET",
    action_type: actionType.GET_FEATURES_LIST,
    description: "",
  },
  // Grant Access
  grant_project_access: {
    api_name: "/PCM/project/access/grant",
    https_method: "POST",
    action_type: "",
    description: "Grant the access for the user of modules in the project!",
  },
  // Project Access
  get_request_access_list: {
    api_name: "/PCM/project/access/requests",
    https_method: "GET",
    action_type: "",
    description: "Get requested access list",
  },
  request_access: {
    api_name: "/PCM/project/access/request",
    https_method: "POST",
    action_type: "",
    description:
      "To request co-owner/editor/viewer access to the project/zone/ee/swm",
  },
  approve_access: {
    api_name: "/PCM/project/access/request/approve",
    https_method: "PUT",
    action_type: "",
    description:
      "Approve co-owner/editor/viewer access to the project/zone/ee/swm",
  },
  reject_access: {
    api_name: "/PCM/project/access/request/reject",
    https_method: "PUT",
    action_type: "",
    description:
      "Reject co-owner/editor/viewer access to the project/zone/ee/swm",
  },
  forward_access: {
    api_name: "/PCM/project/access/request/forward",
    https_method: "POST",
    action_type: "",
    description: "Forward co-owner/editor/viewer access to the super admin",
  },
  get_forward_access: {
    api_name: "/PCM/project/access/requests/superadmin",
    https_method: "GET",
    action_type: "",
    description: "Get forward access list",
  },
  approve_forward_access: {
    api_name: "/PCM/project/access/request/approve/superadmin",
    https_method: "PUT",
    action_type: "",
    description: "Approve forward access list",
  },
  reject_forward_access: {
    api_name: "/PCM/project/access/request/reject/superadmin",
    https_method: "PUT",
    action_type: "",
    description: "Reject forward access list",
  },

  //New Role Adding UCM
  add_role_ddh_ucm: {
    api_name: "/UCM/roles/",
    https_method: "POST",
    action_type: actionType.ADD_ROLE_DDH_UCM,
    description: "Add Roles",
  },
  list_role_ddh_ucm: {
    api_name: "/UCM/roles/",
    https_method: "GET",
    action_type: actionType.LIST_ROLE_DDH_UCM,
    description: "List Roles",
  },
  get_role_ddh_ucm: {
    api_name: "/UCM/roles/{id}",
    https_method: "GET",
    action_type: actionType.GET_ROLE_DDH_UCM,
    description: "Get Roles",
  },
  update_role_ddh_ucm: {
    api_name: "/UCM/roles/{id}",
    https_method: "PUT",
    action_type: actionType.PUT_ROLE_DDH_UCM,
    description: "Put Roles",
  },
  delete_role_ddh_ucm: {
    api_name: "/UCM/roles/{id}",
    https_method: "DELETE",
    action_type: actionType.DELETE_ROLE_DDH_UCM,
    description: "Delete Roles",
  },
  get_roleLS_ddh_ucm: {
    api_name: "/UCM/role-legal-chapters/roles/{id}/legal-chapters",
    https_method: "GET",
    action_type: actionType.GET_ROLE_LR_DDH_UCM,
    description: "Get Role LR",
  },
  add_roleLS_ddh_ucm: {
    api_name: "/UCM/role-legal-chapters/roles/{id}/legal-chapters",
    https_method: "POST",
    action_type: actionType.ADD_ROLE_LR_DDH_UCM,
    description: "Add Role LR",
  },
  delete_roleLS_ddh_ucm: {
    api_name: "/UCM/role-legal-chapters/roles/{id}/legal-chapters",
    https_method: "DELETE",
    action_type: actionType.DELETE_ROLE_LR_DDH_UCM,
    description: "Add Role LR",
  },
  get_roleRP_ddh_ucm: {
    api_name: "/UCM/role-policy/{id}/policies",
    https_method: "GET",
    action_type: actionType.GET_ROLE_RP_DDH_UCM,
    description: "Get Role LR",
  },
  add_roleRP_ddh_ucm: {
    api_name: "/UCM/role-policy/{id}/policies",
    https_method: "POST",
    action_type: actionType.ADD_ROLE_RP_DDH_UCM,
    description: "Add Role LR",
  },
  delete_roleRP_ddh_ucm: {
    api_name: "/UCM/role-policy/{id}/policies",
    https_method: "DELETE",
    action_type: actionType.DELETE_ROLE_RP_DDH_UCM,
    description: "Add Role LR",
  },
};

export default userManagementApis;
